import clsx from 'clsx';
import React from 'react';
import useFormFieldAria from '../../hooks/useFormFieldAria';

type ProgressMoonSize = 'sm' | 'md' | 'lg';

const SIZES: Record<ProgressMoonSize, { width: number; height: number }> = {
  sm: { width: 40, height: 40 },
  md: { width: 50, height: 50 },
  lg: { width: 64, height: 64 },
};

type ProgressMoonProps = {
  id?: string;
  isLabelHidden?: boolean;
  label: string;
  progress?: number;
  size?: ProgressMoonSize;
};

/**
 * ProgressMoon — semicircular gauge (half-moon arc) for a single value (0–100%).
 *
 * `label` is the required accessible name and, by default, the visible caption
 * below the gauge; hide it visually with `isLabelHidden` (kept in the a11y tree
 * via aria-labelledby). Colours come from the SCSS custom properties
 * `--progress-moon-track` / `--progress-moon-fill` (override them from the
 * consumer if a custom colour is ever needed).
 */
function ProgressMoon({ id, isLabelHidden = false, label, progress = 0, size = 'md' }: Readonly<ProgressMoonProps>): JSX.Element {
  const { ids } = useFormFieldAria({ id });

  const { width, height } = SIZES[size] ?? SIZES.md;
  // Guard against NaN/undefined (e.g. a consumer dividing by 0) and clamp 0–100.
  const clampedProgress = Number.isFinite(progress) ? Math.min(Math.max(progress, 0), 100) : 0;

  const radius = Math.min(width, height) / 2;
  const strokeWidth = radius * 0.4;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * Math.PI;
  const offset = circumference - (clampedProgress / 100) * circumference;
  const arcPath = `M ${strokeWidth / 2},${height / 2} A ${normalizedRadius},${normalizedRadius} 0 1,1 ${width - strokeWidth / 2},${height / 2}`;

  return (
    <div
      aria-labelledby={ids.labelId}
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={clampedProgress}
      className="progress-moon"
      role="progressbar"
    >
      <svg className="progress-moon__svg" height={height / 2} viewBox={`0 0 ${width} ${height / 2}`} width={width}>
        <path className="progress-moon__track" d={arcPath} strokeWidth={strokeWidth} />
        <path className="progress-moon__fill" d={arcPath} strokeDasharray={circumference} strokeDashoffset={offset} strokeWidth={strokeWidth} />
      </svg>
      <span className={clsx('progress-moon__label', { 'sr-only': isLabelHidden })} id={ids.labelId}>
        {label}
      </span>
    </div>
  );
}

export default ProgressMoon;
export type { ProgressMoonProps, ProgressMoonSize };
